import { ReactNode } from "react";

import { Stack } from "@mui/material";
import { Control, FieldValues, Path, useController } from "react-hook-form";

import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardSwitch } from "@/dashboard-design-system/Switch";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

export type LiveEditorToggleProps<T extends FieldValues> = {
  label: ReactNode;
  description?: ReactNode;
  name: Path<T>;
  control: Control<T>;
  tooltip?: string;
  disabled?: boolean;
  onValueChange?: (checked: boolean) => void;
  hideLabel?: boolean;
  labelVariant?: "subtitle1" | "body1";
  "data-test"?: string;
};

export const LiveEditorToggle = <T extends FieldValues>({
  label,
  description,
  name,
  control,
  tooltip,
  disabled,
  onValueChange,
  hideLabel = false,
  labelVariant = "subtitle1",
  "data-test": dataTest,
}: LiveEditorToggleProps<T>) => {
  const { field } = useController({
    name,
    control,
  });

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems={description ? "flex-start" : "center"}
      maxWidth="100%"
    >
      <Stack direction="column" spacing={0} paddingRight={1} flex={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          {!hideLabel && (
            <DashboardTypography variant={labelVariant} vibe={disabled ? "text-neutral-supershy" : undefined}>
              {label}
            </DashboardTypography>
          )}
          {tooltip && <DashboardInformationTooltip title={tooltip} slotProps={{ icon: { size: "small" } }} />}
        </Stack>
        {description && !hideLabel && (
          <DashboardTypography variant="body2" vibe="text-neutral-moderate">
            {description}
          </DashboardTypography>
        )}
      </Stack>

      <DashboardSwitch
        name={name}
        checked={field.value}
        disabled={disabled}
        onChange={(_, checked) => {
          field.onChange(checked);
          onValueChange?.(checked);
        }}
        data-test={dataTest}
      />
    </Stack>
  );
};
